import React, { useEffect, useMemo, useState } from 'react';
import { researchosService } from '../../services/researchosService';
import { useSortableRows, SortableTh } from '../../components/tables/SortableTable';

// Phase 1 — deterministic narrative tracker (plan §4/§5-v1). Attention (mention volume),
// favorability (time-decayed net stance), and momentum (Δ favorability) per entity, computed
// entirely from the committed stance graph. No LLM, no tokens. Read-only.
const GOLD = 'rgb(var(--brand-gold-rgb))';
const fav1 = (v) => (v == null ? '—' : (v >= 0 ? '+' : '') + Number(v).toFixed(2));
const favColor = (v) => (v > 0.05 ? 'text-green-600 dark:text-green-400'
  : v < -0.05 ? 'text-red-600 dark:text-red-400' : 'text-gray-400');

function MiniList({ title, rows, metric }) {
  return (
    <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-800">
      <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400">{title}</div>
      <div className="flex flex-col gap-1">
        {rows.length === 0 && <div className="text-[11px] text-gray-400">—</div>}
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between text-xs">
            <span className="font-medium text-gray-800 dark:text-gray-100">{r.ticker || r.name}</span>
            <span className={`tabular-nums ${favColor(r[metric])}`}>{fav1(r[metric])}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Monthly attention volume, stacked by stance (bull green / bear red / neutral gray).
function VolumeChart({ timeline }) {
  const max = Math.max(1, ...timeline.map((m) => m.bull + m.bear + m.neutral));
  return (
    <div>
      <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.15em]" style={{ color: GOLD }}>
        Chatter volume over time
      </div>
      <div className="flex items-end gap-0.5 overflow-x-auto scrollbar-thin" style={{ height: 110 }}>
        {timeline.map((m) => {
          const total = m.bull + m.bear + m.neutral;
          const h = (n) => `${(n / max) * 100}%`;
          return (
            <div key={m.month} className="flex min-w-[10px] flex-1 flex-col justify-end" title={`${m.month}: ${total} claims (${m.bull}▲ ${m.bear}▼ ${m.neutral}•)`}>
              <div style={{ height: h(m.bull), background: '#16a34a' }} />
              <div style={{ height: h(m.bear), background: '#dc2626' }} />
              <div style={{ height: h(m.neutral), background: '#9ca3af55' }} />
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-[9px] text-gray-400">
        <span>{timeline[0]?.month}</span><span>{timeline[timeline.length - 1]?.month}</span>
      </div>
    </div>
  );
}

const COLS = [
  { key: 'name', type: 'string', get: (r) => r.ticker || r.name },
  { key: 'mentions', type: 'number', get: (r) => r.mentions },
  { key: 'favorability', type: 'number', get: (r) => r.favorability },
  { key: 'momentum', type: 'abs', get: (r) => r.momentum },
];

export default function NarrativeTracker() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [tradeableOnly, setTradeableOnly] = useState(false);

  useEffect(() => {
    researchosService.getNarrativeTracker()
      .then((r) => setData(r.data))
      .catch(() => setErr('Could not load the tracker.'));
  }, []);

  const rows = useMemo(() => {
    const lb = data?.leaderboard || [];
    return tradeableOnly ? lb.filter((r) => r.tradeable) : lb;
  }, [data, tradeableOnly]);
  const { sorted, toggle, indicator } = useSortableRows(rows, COLS, { key: 'mentions', dir: 'desc' });

  if (err) return <div className="text-xs text-red-500">{err}</div>;
  if (!data) return <div className="text-xs text-gray-400">Loading tracker…</div>;

  const asOf = data.as_of ? new Date(data.as_of).toLocaleDateString() : '';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="text-[10px] font-medium uppercase tracking-[0.15em]" style={{ color: GOLD }}>
            Narrative Tracker
          </div>
          <div className="text-[11px] text-gray-400">
            deterministic · {data.n_entities} entities · net stance decayed (½-life {data.params?.halflife_days}d) · as-of {asOf}
          </div>
        </div>
        <label className="flex items-center gap-1.5 text-[11px] text-gray-500">
          <input type="checkbox" checked={tradeableOnly} onChange={(e) => setTradeableOnly(e.target.checked)} />
          US-listed only
        </label>
      </div>

      {/* winners / losers / movers */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <MiniList title="Most favored" rows={data.winners} metric="favorability" />
        <MiniList title="Least favored" rows={data.losers} metric="favorability" />
        <MiniList title="Biggest movers" rows={data.movers} metric="momentum" />
      </div>

      {data.timeline?.length > 0 && <VolumeChart timeline={data.timeline} />}

      {/* leaderboard */}
      <div className="overflow-x-auto scrollbar-thin rounded-xl border border-gray-200 dark:border-gray-800">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200 text-gray-400 dark:border-gray-800">
              <SortableTh label="Entity" colKey="name" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
              <th className="px-3 py-2 text-left font-medium">Sector</th>
              <SortableTh label="Mentions" colKey="mentions" toggle={toggle} indicator={indicator} className="px-3 py-2" />
              <SortableTh label="Favorability" colKey="favorability" toggle={toggle} indicator={indicator} className="px-3 py-2" />
              <SortableTh label="Momentum" colKey="momentum" toggle={toggle} indicator={indicator} className="px-3 py-2" />
              <th className="px-3 py-2 text-right font-medium">▲ / ▼ / •</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => (
              <tr key={i} className="border-b border-gray-100 last:border-0 dark:border-gray-800/60">
                <td className="px-3 py-1.5">
                  <span className="font-medium text-gray-900 dark:text-gray-100">{r.ticker || r.name}</span>
                  {!r.tradeable && <span className="ml-1 text-[9px] uppercase tracking-wider text-amber-500">{r.coverage === 'not_covered' ? 'private' : 'n/a'}</span>}
                  {r.ticker && r.name !== r.ticker && <span className="ml-1 text-gray-400">{r.name}</span>}
                </td>
                <td className="px-3 py-1.5 text-left text-gray-400">{r.sector || '—'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-gray-700 dark:text-gray-300">{r.mentions}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${favColor(r.favorability)}`}>{fav1(r.favorability)}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${favColor(r.momentum)}`}>{r.momentum ? fav1(r.momentum) : '—'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-gray-400">
                  <span className="text-green-600 dark:text-green-400">{r.bull}</span> / <span className="text-red-600 dark:text-red-400">{r.bear}</span> / {r.neutral}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-gray-400">
        Favorability = time-decayed net stance of the company's claims in [−1,+1] (bull +1, bear −1, neutral 0; weighted by conviction × echo). Momentum = last-{data.params?.window_days}d vs prior window. Mentions = documents.
        Weights are hypotheses to validate against price — not final.
      </div>
    </div>
  );
}
